import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import AdminLayout from '../../components/layout/AdminLayout';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../../components/ui/select';
import { Label } from '../../components/ui/label';
import { Checkbox } from '../../components/ui/checkbox';
import { Progress } from '../../components/ui/progress';
import {
  ArrowLeft,
  GraduationCap,
  User,
  FileText,
  CheckSquare,
  Clock,
  MessageSquare,
  Upload,
  Loader2,
  Download,
  Plus,
  Building2,
  Users,
  Shield,
  BookOpen,
  Globe,
  Briefcase,
  Award,
  AlertTriangle,
  ExternalLink,
  RefreshCw,
  ChevronRight,
  MapPin,
  Mail,
  Phone,
  Calendar,
  DollarSign,
  Target
} from 'lucide-react';
import { toast } from 'sonner';
import { admissionsAPI, adminAPI } from '../../services/api';

const STATUS_COLORS = {
  'Assigned': 'bg-blue-100 text-blue-700 border-blue-200',
  'Docs Pending': 'bg-amber-100 text-amber-700 border-amber-200',
  'Docs Verified': 'bg-cyan-100 text-cyan-700 border-cyan-200',
  'Submitted': 'bg-indigo-100 text-indigo-700 border-indigo-200',
  'Under Review': 'bg-purple-100 text-purple-700 border-purple-200',
  'Offer Received': 'bg-emerald-100 text-emerald-700 border-emerald-200',
  'Accepted': 'bg-green-100 text-green-700 border-green-200',
  'Visa Processing': 'bg-orange-100 text-orange-700 border-orange-200',
  'Completed': 'bg-green-200 text-green-800 border-green-300',
  'Rejected': 'bg-red-100 text-red-700 border-red-200'
};

const STATUS_FLOW = [
  'Assigned', 'Docs Pending', 'Docs Verified', 'Submitted',
  'Under Review', 'Offer Received', 'Accepted', 'Visa Processing', 'Completed'
];

const TIMELINE_ICONS = {
  student: 'bg-blue-100 text-blue-600',
  agent: 'bg-emerald-100 text-emerald-600',
  super_admin: 'bg-purple-100 text-purple-600'
};

// â”€â”€ Helpers â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€

function InfoRow({ label, value, icon: Icon }) {
  return (
    <div className="flex items-start justify-between py-2">
      <span className="text-gray-500 text-sm flex items-center gap-1.5">
        {Icon && <Icon className="w-3.5 h-3.5" />}
        {label}
      </span>
      <span className="font-medium text-sm text-gray-900 text-right max-w-[60%]">{value || '-'}</span>
    </div>
  );
}

function SectionCard({ title, icon: Icon, children, className = '' }) {
  return (
    <Card className={`p-5 bg-white border shadow-sm ${className}`}>
      <h3 className="font-semibold text-gray-900 flex items-center gap-2 mb-4 pb-3 border-b border-stone-100">
        {Icon && <Icon className="w-4 h-4 text-blue-600" />}
        {title}
      </h3>
      {children}
    </Card>
  );
}

// â”€â”€ Main Component â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€

export default function AdminAdmissionDetails() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [application, setApplication] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');

  // Student profile
  const [studentProfile, setStudentProfile] = useState(null);
  const [loadingProfile, setLoadingProfile] = useState(false);

  // Status update
  const [updatingStatus, setUpdatingStatus] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState('');

  // Document upload
  const [uploading, setUploading] = useState(false);
  const [docName, setDocName] = useState('');
  const [docType, setDocType] = useState('');

  // Remark
  const [remarkText, setRemarkText] = useState('');
  const [addingRemark, setAddingRemark] = useState(false);

  // Checklist
  const [newChecklistItem, setNewChecklistItem] = useState('');
  const [updatingChecklist, setUpdatingChecklist] = useState(false);

  // â”€â”€ Data fetching â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€

  const fetchApplication = useCallback(async () => {
    try {
      const data = await admissionsAPI.getAdminById(id);
      setApplication(data);
    } catch (err) {
      console.error('Failed to fetch application:', err);
      toast.error('Failed to load application details');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchApplication();
  }, [fetchApplication]);

  // Fetch full student profile when Student Profile tab is selected
  useEffect(() => {
    if (activeTab === 'student-profile' && application?.studentId && !studentProfile) {
      setLoadingProfile(true);
      adminAPI.getStudentById(application.studentId)
        .then(data => {
          setStudentProfile(data.student || data);
        })
        .catch(err => {
          console.error('Failed to load student profile:', err);
          toast.error('Failed to load student profile');
        })
        .finally(() => setLoadingProfile(false));
    }
  }, [activeTab, application?.studentId, studentProfile]);

  // â”€â”€ Handlers â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€

  const handleStatusUpdate = async () => {
    if (!selectedStatus) return;
    setUpdatingStatus(true);
    try {
      await admissionsAPI.adminUpdateStatus(id, { status: selectedStatus });
      toast.success(`Status updated to "${selectedStatus}"`);
      setSelectedStatus('');
      fetchApplication();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to update status');
    } finally {
      setUpdatingStatus(false);
    }
  };

  const handleDocUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('document', file);
      if (docName) formData.append('docName', docName);
      if (docType) formData.append('docType', docType);
      await admissionsAPI.adminUploadDoc(id, formData);
      toast.success('Document uploaded');
      setDocName('');
      setDocType('');
      fetchApplication();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Upload failed');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const handleAddRemark = async () => {
    if (!remarkText.trim()) return;
    setAddingRemark(true);
    try {
      await admissionsAPI.adminAddRemark(id, { text: remarkText.trim() });
      toast.success('Remark added');
      setRemarkText('');
      fetchApplication();
    } catch (err) {
      toast.error('Failed to add remark');
    } finally {
      setAddingRemark(false);
    }
  };

  const handleChecklistToggle = async (index, completed) => {
    setUpdatingChecklist(true);
    try {
      await admissionsAPI.adminUpdateChecklist(id, { index, completed });
      fetchApplication();
    } catch (err) {
      toast.error('Failed to update checklist');
    } finally {
      setUpdatingChecklist(false);
    }
  };

  const handleAddChecklistItem = async () => {
    if (!newChecklistItem.trim()) return;
    setUpdatingChecklist(true);
    try {
      await admissionsAPI.adminUpdateChecklist(id, { item: newChecklistItem.trim() });
      setNewChecklistItem('');
      fetchApplication();
    } catch (err) {
      toast.error('Failed to add checklist item');
    } finally {
      setUpdatingChecklist(false);
    }
  };

  // â”€â”€ Render guards â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500" />
        </div>
      </AdminLayout>
    );
  }

  if (!application) {
    return (
      <AdminLayout>
        <div className="text-center py-12">
          <h2 className="text-lg font-semibold text-gray-700">Application not found</h2>
          <Button variant="outline" className="mt-4" onClick={() => navigate('/admin/admissions')}>
            <ArrowLeft className="w-4 h-4 mr-2" /> Back to Admissions
          </Button>
        </div>
      </AdminLayout>
    );
  }

  const nextStatuses = application.nextStatuses || [];
  const isTerminal = ['Completed', 'Rejected'].includes(application.status);
  const currentFlowIndex = STATUS_FLOW.indexOf(application.status);
  const progressPercent = application.status === 'Rejected' ? 0 : currentFlowIndex >= 0 ? Math.round(((currentFlowIndex + 1) / STATUS_FLOW.length) * 100) : 0;
  const checklistDone = application.checklist?.filter(c => c.completed).length || 0;
  const checklistTotal = application.checklist?.length || 0;

  return (
    <AdminLayout>
      <div className="space-y-6">

        {/* â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â• HEADER â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â• */}
        <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="sm" onClick={() => navigate('/admin/admissions')}>
              <ArrowLeft className="w-4 h-4 mr-1" /> Back
            </Button>
            <div>
              <h1 className="text-xl font-outfit font-bold text-gray-900 flex items-center gap-2">
                <GraduationCap className="w-5 h-5 text-blue-600" />
                {application.universityName}
              </h1>
              <p className="text-gray-500 text-sm">
                {application.programName}
                {application.programLevel && <span className="text-gray-400"> ({application.programLevel})</span>}
                {' '} &middot; {application.intake}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <Badge className="bg-blue-50 text-blue-700 border border-blue-200">
              <Shield className="w-3 h-3 mr-1" /> Admin Control
            </Badge>
            <Badge variant="outline" className="capitalize text-xs">{application.assignedBy} assigned</Badge>
            <Badge className={`text-sm px-3 py-1 border ${STATUS_COLORS[application.status] || 'bg-stone-100 text-gray-700'}`}>
              {application.status}
            </Badge>
          </div>
        </div>

        {/* â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â• STATUS PROGRESS BAR â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â• */}
        <Card className="p-4 bg-white border shadow-sm">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-medium text-gray-500 uppercase tracking-wider">Application Progress</span>
            <span className="text-xs text-gray-500">{progressPercent}%</span>
          </div>
          <Progress value={progressPercent} className="h-2 mb-3" />
          <div className="flex justify-between overflow-x-auto gap-1">
            {STATUS_FLOW.map((s, i) => {
              const isCurrent = application.status === s;
              const isPast = currentFlowIndex > i;
              return (
                <div key={s} className="flex flex-col items-center min-w-0 flex-1">
                  <div className={`w-3 h-3 rounded-full mb-1 ${isCurrent ? 'bg-blue-600 ring-2 ring-blue-200' : isPast ? 'bg-green-500' : 'bg-gray-200'}`} />
                  <span className={`text-[10px] text-center leading-tight ${isCurrent ? 'text-blue-700 font-semibold' : isPast ? 'text-green-600' : 'text-gray-400'}`}>
                    {s}
                  </span>
                </div>
              );
            })}
          </div>
          {application.status === 'Rejected' && (
            <div className="mt-3 p-2 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2">
              <AlertTriangle className="w-4 h-4 text-red-500 flex-shrink-0" />
              <span className="text-sm text-red-700">This application has been rejected.</span>
            </div>
          )}
        </Card>

        {/* â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â• STATUS TRANSITION â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â• */}
        {nextStatuses.length > 0 && (
          <Card className="p-4 bg-white border shadow-sm">
            <div className="flex flex-col sm:flex-row sm:items-center gap-3">
              <div className="flex items-center gap-2">
                <Label className="text-sm font-medium whitespace-nowrap">Update Status:</Label>
                <Badge variant="outline" className="text-xs">{application.status}</Badge>
                <ChevronRight className="w-4 h-4 text-gray-400" />
              </div>
              <Select value={selectedStatus} onValueChange={setSelectedStatus}>
                <SelectTrigger className="w-52">
                  <SelectValue placeholder="Select next status" />
                </SelectTrigger>
                <SelectContent>
                  {nextStatuses.map(s => (
                    <SelectItem key={s} value={s}>{s}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button
                onClick={handleStatusUpdate}
                disabled={!selectedStatus || updatingStatus}
                className="bg-blue-600 hover:bg-blue-700"
                size="sm"
              >
                {updatingStatus && <Loader2 className="w-4 h-4 mr-1 animate-spin" />}
                Update Status
              </Button>
            </div>
          </Card>
        )}

        {/* â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â• QUICK STATS ROW â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â• */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="p-3 bg-white border shadow-sm">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 bg-blue-100 rounded-lg flex items-center justify-center">
                <FileText className="w-4 h-4 text-blue-600" />
              </div>
              <div>
                <p className="text-lg font-bold text-gray-900">{application.documents?.length || 0}</p>
                <p className="text-xs text-gray-500">Documents</p>
              </div>
            </div>
          </Card>
          <Card className="p-3 bg-white border shadow-sm">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 bg-emerald-100 rounded-lg flex items-center justify-center">
                <CheckSquare className="w-4 h-4 text-emerald-600" />
              </div>
              <div>
                <p className="text-lg font-bold text-gray-900">{checklistDone}/{checklistTotal}</p>
                <p className="text-xs text-gray-500">Checklist</p>
              </div>
            </div>
          </Card>
          <Card className="p-3 bg-white border shadow-sm">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 bg-purple-100 rounded-lg flex items-center justify-center">
                <Clock className="w-4 h-4 text-purple-600" />
              </div>
              <div>
                <p className="text-lg font-bold text-gray-900">{application.timeline?.length || 0}</p>
                <p className="text-xs text-gray-500">Events</p>
              </div>
            </div>
          </Card>
          <Card className="p-3 bg-white border shadow-sm">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 bg-amber-100 rounded-lg flex items-center justify-center">
                <MessageSquare className="w-4 h-4 text-amber-600" />
              </div>
              <div>
                <p className="text-lg font-bold text-gray-900">{application.remarks?.length || 0}</p>
                <p className="text-xs text-gray-500">Remarks</p>
              </div>
            </div>
          </Card>
        </div>

        {/* â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â• TABS â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â• */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="bg-white border shadow-sm flex-wrap h-auto gap-1 p-1">
            <TabsTrigger value="overview" className="flex items-center gap-1.5 text-xs sm:text-sm">
              <Building2 className="w-3.5 h-3.5" /> Overview
            </TabsTrigger>
            <TabsTrigger value="documents" className="flex items-center gap-1.5 text-xs sm:text-sm">
              <FileText className="w-3.5 h-3.5" /> Documents
              {application.documents?.length > 0 && (
                <Badge variant="secondary" className="text-[10px] ml-0.5 px-1.5">{application.documents.length}</Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="checklist" className="flex items-center gap-1.5 text-xs sm:text-sm">
              <CheckSquare className="w-3.5 h-3.5" /> Checklist
            </TabsTrigger>
            <TabsTrigger value="timeline" className="flex items-center gap-1.5 text-xs sm:text-sm">
              <Clock className="w-3.5 h-3.5" /> Timeline
            </TabsTrigger>
            <TabsTrigger value="remarks" className="flex items-center gap-1.5 text-xs sm:text-sm">
              <MessageSquare className="w-3.5 h-3.5" /> Remarks
              {application.remarks?.length > 0 && (
                <Badge variant="secondary" className="text-[10px] ml-0.5 px-1.5">{application.remarks.length}</Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="student-profile" className="flex items-center gap-1.5 text-xs sm:text-sm">
              <User className="w-3.5 h-3.5" /> Student Profile
            </TabsTrigger>
          </TabsList>

          {/* â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€ OVERVIEW TAB â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€ */}
          <TabsContent value="overview">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {/* Student Card */}
              <SectionCard title="Student" icon={User}>
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-full flex items-center justify-center text-white font-bold text-lg">
                    {application.student?.firstName?.[0]}{application.student?.lastName?.[0]}
                  </div>
                  <div>
                    <p className="font-semibold text-gray-900">{application.student?.firstName} {application.student?.lastName}</p>
                    <p className="text-xs text-gray-500">{application.student?.email}</p>
                  </div>
                </div>
                <div className="space-y-0.5 divide-y divide-gray-50">
                  <InfoRow label="Phone" value={application.student?.phone} icon={Phone} />
                  <InfoRow label="Country" value={application.student?.country} icon={MapPin} />
                  <InfoRow label="Student ID" value={application.studentId} />
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  className="w-full mt-4"
                  onClick={() => navigate(`/admin/students/${application.studentId}`)}
                >
                  <ExternalLink className="w-3.5 h-3.5 mr-1.5" /> View Full Profile
                </Button>
              </SectionCard>

              {/* Application Card */}
              <SectionCard title="Application Details" icon={Building2}>
                <div className="space-y-0.5 divide-y divide-gray-50">
                  <InfoRow label="University" value={application.universityName} icon={GraduationCap} />
                  <InfoRow label="Program" value={application.programName} icon={BookOpen} />
                  {application.programLevel && <InfoRow label="Level" value={application.programLevel} />}
                  <InfoRow label="Intake" value={application.intake} icon={Calendar} />
                  <InfoRow label="Status" value={
                    <Badge className={`${STATUS_COLORS[application.status]} border`}>{application.status}</Badge>
                  } />
                  {application.subStatus && <InfoRow label="Sub-status" value={application.subStatus} />}
                  <InfoRow label="Assigned By" value={
                    <Badge variant="outline" className="capitalize">{application.assignedBy}</Badge>
                  } />
                  <InfoRow label="Created" value={new Date(application.createdAt).toLocaleDateString()} icon={Calendar} />
                  <InfoRow label="Last Updated" value={new Date(application.updatedAt).toLocaleString()} />
                </div>
              </SectionCard>

              {/* Agent Card */}
              <SectionCard title="Assigned Agent" icon={Users}>
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-full flex items-center justify-center text-white font-bold text-lg">
                    {application.agent?.firstName?.[0]}{application.agent?.lastName?.[0]}
                  </div>
                  <div>
                    <p className="font-semibold text-gray-900">{application.agent?.firstName} {application.agent?.lastName}</p>
                    <p className="text-xs text-gray-500">{application.agent?.email}</p>
                  </div>
                </div>
                <div className="space-y-0.5 divide-y divide-gray-50">
                  <InfoRow label="Agent ID" value={
                    <span className="text-xs text-gray-400 font-mono">{application.agentId}</span>
                  } />
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  className="w-full mt-4"
                  onClick={() => navigate(`/admin/agents/${application.agentId}`)}
                >
                  <ExternalLink className="w-3.5 h-3.5 mr-1.5" /> View Agent
                </Button>
              </SectionCard>
            </div>
          </TabsContent>

          {/* â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€ DOCUMENTS TAB â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€ */}
          <TabsContent value="documents">
            <Card className="p-6 bg-white border shadow-sm space-y-4">
              {/* Upload section */}
              <div className="flex flex-col sm:flex-row items-end gap-3 pb-4 border-b border-stone-100">
                <div className="flex-1 w-full">
                  <Label className="text-sm">Document Name</Label>
                  <Input
                    value={docName}
                    onChange={(e) => setDocName(e.target.value)}
                    placeholder="e.g. Transcript, Offer Letter, SOP"
                    className="mt-1"
                  />
                </div>
                <div className="w-full sm:w-44">
                  <Label className="text-sm">Type</Label>
                  <Select value={docType} onValueChange={setDocType}>
                    <SelectTrigger className="mt-1">
                      <SelectValue placeholder="Select type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="transcript">Transcript</SelectItem>
                      <SelectItem value="sop">SOP</SelectItem>
                      <SelectItem value="recommendation">Recommendation</SelectItem>
                      <SelectItem value="offer_letter">Offer Letter</SelectItem>
                      <SelectItem value="visa_docs">Visa Documents</SelectItem>
                      <SelectItem value="financial">Financial Docs</SelectItem>
                      <SelectItem value="identity">Identity / Passport</SelectItem>
                      <SelectItem value="other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <input
                    type="file"
                    id="admin-doc-upload"
                    className="hidden"
                    onChange={handleDocUpload}
                    accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.jpg,.jpeg,.png,.gif,.webp"
                  />
                  <Button
                    onClick={() => document.getElementById('admin-doc-upload').click()}
                    disabled={uploading}
                    className="bg-blue-600 hover:bg-blue-700"
                  >
                    {uploading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
                    Upload
                  </Button>
                </div>
              </div>

              {/* Document list */}
              {application.documents?.length > 0 ? (
                <div className="space-y-2">
                  {application.documents.map((doc) => (
                    <div key={doc.docId} className="flex items-center justify-between p-3 bg-stone-50 rounded-lg hover:bg-stone-100 transition-colors">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                          <FileText className="w-5 h-5 text-blue-600" />
                        </div>
                        <div>
                          <p className="font-medium text-sm text-gray-900">{doc.name}</p>
                          <div className="flex items-center gap-2 mt-0.5">
                            {doc.type && <Badge variant="outline" className="text-[10px] py-0">{doc.type}</Badge>}
                            <span className="text-xs text-gray-400">
                              by <span className="capitalize">{doc.uploadedByRole}</span> &middot; {new Date(doc.uploadedAt).toLocaleDateString()}
                            </span>
                          </div>
                        </div>
                      </div>
                      <a
                        href={doc.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="p-2 text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded-lg transition-colors"
                      >
                        <Download className="w-4 h-4" />
                      </a>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <FileText className="w-10 h-10 text-gray-300 mx-auto mb-2" />
                  <p className="text-gray-500 text-sm">No documents uploaded yet.</p>
                  <p className="text-gray-400 text-xs mt-1">Upload documents using the form above.</p>
                </div>
              )}
            </Card>
          </TabsContent>

          {/* â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€ CHECKLIST TAB â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€ */}
          <TabsContent value="checklist">
            <Card className="p-6 bg-white border shadow-sm space-y-4">
              {/* Progress bar */}
              {checklistTotal > 0 && (
                <div className="pb-4 border-b border-stone-100">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-gray-700">Completion</span>
                    <span className="text-sm text-gray-500">{checklistDone} of {checklistTotal} items</span>
                  </div>
                  <Progress value={checklistTotal > 0 ? (checklistDone / checklistTotal) * 100 : 0} className="h-2" />
                </div>
              )}

              {/* Add item */}
              <div className="flex items-center gap-3 pb-4 border-b border-stone-100">
                <Input
                  value={newChecklistItem}
                  onChange={(e) => setNewChecklistItem(e.target.value)}
                  placeholder="Add checklist item..."
                  className="flex-1"
                  onKeyDown={(e) => e.key === 'Enter' && handleAddChecklistItem()}
                />
                <Button
                  onClick={handleAddChecklistItem}
                  disabled={updatingChecklist || !newChecklistItem.trim()}
                  size="sm"
                  className="bg-blue-600 hover:bg-blue-700"
                >
                  <Plus className="w-4 h-4 mr-1" /> Add
                </Button>
              </div>

              {application.checklist?.length > 0 ? (
                <div className="space-y-1">
                  {application.checklist.map((item, idx) => (
                    <div key={idx} className="flex items-center gap-3 p-2.5 rounded-lg hover:bg-stone-50 transition-colors">
                      <Checkbox
                        checked={item.completed}
                        onCheckedChange={(checked) => handleChecklistToggle(idx, checked)}
                        disabled={updatingChecklist}
                      />
                      <span className={`text-sm flex-1 ${item.completed ? 'line-through text-gray-400' : 'text-gray-700'}`}>
                        {item.item}
                      </span>
                      {item.completedAt && (
                        <span className="text-xs text-gray-400">
                          {new Date(item.completedAt).toLocaleDateString()}
                        </span>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <CheckSquare className="w-10 h-10 text-gray-300 mx-auto mb-2" />
                  <p className="text-gray-500 text-sm">No checklist items.</p>
                  <p className="text-gray-400 text-xs mt-1">Add items to track application requirements.</p>
                </div>
              )}
            </Card>
          </TabsContent>

          {/* â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€ TIMELINE TAB â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€ */}
          <TabsContent value="timeline">
            <Card className="p-6 bg-white border shadow-sm">
              <div className="flex items-center justify-between mb-4 pb-3 border-b border-stone-100">
                <h3 className="font-semibold text-gray-900 flex items-center gap-2">
                  <Clock className="w-4 h-4 text-blue-600" /> Audit Trail
                </h3>
                <span className="text-xs text-gray-400">{application.timeline?.length || 0} events (immutable)</span>
              </div>

              {application.timeline?.length > 0 ? (
                <div className="relative">
                  <div className="absolute left-[15px] top-0 bottom-0 w-0.5 bg-gray-200" />
                  <div className="space-y-0">
                    {[...application.timeline].reverse().map((entry, idx) => {
                      const colorClass = TIMELINE_ICONS[entry.byRole] || 'bg-stone-100 text-gray-600';
                      return (
                        <div key={idx} className="flex gap-4 relative group">
                          <div className={`w-8 h-8 rounded-full flex items-center justify-center z-10 flex-shrink-0 ${colorClass}`}>
                            {entry.byRole === 'student' ? <User className="w-3.5 h-3.5" /> :
                             entry.byRole === 'agent' ? <Users className="w-3.5 h-3.5" /> :
                             <Shield className="w-3.5 h-3.5" />}
                          </div>
                          <div className="flex-1 pb-5">
                            <p className="text-sm font-medium text-gray-900">{entry.action}</p>
                            <div className="flex items-center gap-2 mt-1">
                              <Badge variant="outline" className="text-[10px] capitalize py-0">{entry.byRole}</Badge>
                              <span className="text-xs text-gray-400">
                                {new Date(entry.date).toLocaleString()}
                              </span>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ) : (
                <div className="text-center py-8">
                  <Clock className="w-10 h-10 text-gray-300 mx-auto mb-2" />
                  <p className="text-gray-500 text-sm">No timeline entries yet.</p>
                </div>
              )}
            </Card>
          </TabsContent>

          {/* â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€ REMARKS TAB â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€ */}
          <TabsContent value="remarks">
            <Card className="p-6 bg-white border shadow-sm space-y-4">
              {/* Add remark */}
              <div className="pb-4 border-b border-stone-100 space-y-3">
                <Label className="text-sm font-medium">Add Admin Remark / Guidance</Label>
                <Textarea
                  value={remarkText}
                  onChange={(e) => setRemarkText(e.target.value)}
                  placeholder="Add instructions for the agent, internal notes, or guidance for processing this application..."
                  rows={4}
                />
                <div className="flex justify-end">
                  <Button
                    onClick={handleAddRemark}
                    disabled={addingRemark || !remarkText.trim()}
                    size="sm"
                    className="bg-blue-600 hover:bg-blue-700"
                  >
                    {addingRemark && <Loader2 className="w-4 h-4 mr-1 animate-spin" />}
                    Add Remark
                  </Button>
                </div>
              </div>

              {application.remarks?.length > 0 ? (
                <div className="space-y-3">
                  {[...application.remarks].reverse().map((remark, idx) => (
                    <div key={idx} className="p-4 bg-stone-50 rounded-lg border border-stone-100">
                      <p className="text-sm text-gray-700 whitespace-pre-wrap">{remark.text}</p>
                      <div className="flex items-center gap-2 mt-3 pt-2 border-t border-stone-100">
                        <Badge variant="outline" className="text-[10px] capitalize py-0">{remark.byRole}</Badge>
                        <span className="text-xs text-gray-400">
                          {new Date(remark.date).toLocaleString()}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <MessageSquare className="w-10 h-10 text-gray-300 mx-auto mb-2" />
                  <p className="text-gray-500 text-sm">No remarks yet.</p>
                  <p className="text-gray-400 text-xs mt-1">Add remarks to provide guidance or audit notes.</p>
                </div>
              )}
            </Card>
          </TabsContent>

          {/* â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€ STUDENT PROFILE TAB â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€ */}
          <TabsContent value="student-profile">
            {loadingProfile ? (
              <Card className="p-12 bg-white border shadow-sm flex items-center justify-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500" />
              </Card>
            ) : studentProfile ? (
              <StudentProfilePanel student={studentProfile} navigate={navigate} />
            ) : (
              <Card className="p-12 bg-white border shadow-sm text-center">
                <User className="w-10 h-10 text-gray-300 mx-auto mb-2" />
                <p className="text-gray-500">Unable to load student profile.</p>
                <Button
                  variant="outline"
                  size="sm"
                  className="mt-3"
                  onClick={() => {
                    setStudentProfile(null);
                    setActiveTab('overview');
                    setTimeout(() => setActiveTab('student-profile'), 50);
                  }}
                >
                  <RefreshCw className="w-3.5 h-3.5 mr-1.5" /> Retry
                </Button>
              </Card>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </AdminLayout>
  );
}

// â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•
// STUDENT PROFILE PANEL (sub-component)
// â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•

function StudentProfilePanel({ student, navigate }) {
  const user = student.user || student;
  const profile = student;

  const docs = profile.documents || {};
  const docEntries = [
    { label: 'Transcripts', key: 'transcripts', url: docs.transcripts },
    { label: 'Test Scores', key: 'testScores', url: docs.testScores },
    { label: 'SOP', key: 'sop', url: docs.sop },
    { label: 'Recommendation', key: 'recommendation', url: docs.recommendation },
    { label: 'Resume / CV', key: 'resume', url: docs.resume },
    { label: 'Passport', key: 'passport', url: docs.passport },
  ];

  const serviceRequests = student.serviceRequests || [];

  return (
    <div className="space-y-6">
      {/* Personal Info + Academics */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Personal */}
        <SectionCard title="Personal Information" icon={User}>
          <div className="flex items-center gap-4 mb-4">
            {user.avatar ? (
              <img src={user.avatar} alt="Avatar" className="w-14 h-14 rounded-full object-cover" />
            ) : (
              <div className="w-14 h-14 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-full flex items-center justify-center text-white font-bold text-xl">
                {user.firstName?.[0]}{user.lastName?.[0]}
              </div>
            )}
            <div>
              <p className="font-semibold text-gray-900 text-lg">{user.firstName} {user.lastName}</p>
              <p className="text-sm text-gray-500">{user.role || 'student'}</p>
            </div>
          </div>
          <div className="space-y-0.5 divide-y divide-gray-50">
            <InfoRow label="Email" value={user.email} icon={Mail} />
            <InfoRow label="Phone" value={user.phone} icon={Phone} />
            <InfoRow label="Country" value={user.country || profile.country} icon={MapPin} />
            <InfoRow label="Age" value={profile.age} />
            <InfoRow label="Student ID" value={profile.studentId} />
            <InfoRow label="Status" value={
              <Badge variant="outline" className="capitalize">{profile.status || 'active'}</Badge>
            } />
            <InfoRow label="Onboarding" value={profile.onboardingCompleted ? 'Completed' : 'Pending'} />
          </div>
        </SectionCard>

        {/* Academic */}
        <SectionCard title="Academic Profile" icon={GraduationCap}>
          <div className="space-y-0.5 divide-y divide-gray-50">
            <InfoRow label="Education Level" value={
              profile.currentEducationLevel
                ? profile.currentEducationLevel.charAt(0).toUpperCase() + profile.currentEducationLevel.slice(1)
                : null
            } icon={BookOpen} />
            <InfoRow label="Field of Study" value={profile.fieldOfStudy} />
            <InfoRow label="Institution" value={profile.institution} icon={Building2} />
            <InfoRow label="GPA" value={profile.gpa} icon={Award} />
            <InfoRow label="Graduation Year" value={profile.graduationYear} icon={Calendar} />
          </div>

          {/* Test Scores */}
          <h4 className="font-medium text-gray-800 mt-5 mb-3 pt-3 border-t border-stone-100 flex items-center gap-1.5">
            <Award className="w-3.5 h-3.5 text-blue-600" /> Test Scores
          </h4>
          <div className="grid grid-cols-3 gap-3">
            {[
              { label: 'IELTS', value: profile.ielts },
              { label: 'TOEFL', value: profile.toefl },
              { label: 'GRE', value: profile.gre }
            ].map(score => (
              <div key={score.label} className="bg-stone-50 rounded-lg p-3 text-center">
                <p className="text-xs text-gray-500 mb-1">{score.label}</p>
                <p className={`text-lg font-bold ${score.value ? 'text-gray-900' : 'text-gray-300'}`}>
                  {score.value || '--'}
                </p>
              </div>
            ))}
          </div>
        </SectionCard>
      </div>

      {/* Preferences + Career */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Preferences */}
        <SectionCard title="Preferences" icon={Globe}>
          <div className="space-y-0.5 divide-y divide-gray-50">
            <InfoRow label="Preferred Countries" value={
              profile.preferredCountries?.length > 0
                ? profile.preferredCountries.join(', ')
                : null
            } icon={MapPin} />
            <InfoRow label="Interested Countries" value={
              profile.interestedCountries?.length > 0
                ? profile.interestedCountries.join(', ')
                : null
            } />
            <InfoRow label="Preferred Degree" value={
              profile.preferredDegreeLevel
                ? profile.preferredDegreeLevel.charAt(0).toUpperCase() + profile.preferredDegreeLevel.slice(1)
                : null
            } icon={GraduationCap} />
            <InfoRow label="Preferred Destination" value={profile.preferredDestination} />
            <InfoRow label="Budget" value={profile.budget} icon={DollarSign} />
            <InfoRow label="Intake" value={profile.intake} icon={Calendar} />
          </div>
        </SectionCard>

        {/* Career */}
        <SectionCard title="Career & Goals" icon={Briefcase}>
          <div className="space-y-0.5 divide-y divide-gray-50">
            <InfoRow label="Career Goals" value={profile.careerGoals} icon={Target} />
            <InfoRow label="Industry" value={
              profile.industry
                ? profile.industry.charAt(0).toUpperCase() + profile.industry.slice(1)
                : null
            } icon={Briefcase} />
            <InfoRow label="Work Location" value={
              profile.workLocation
                ? profile.workLocation.replace(/-/g, ' ').replace(/\b\w/g, c => c.toUpperCase())
                : null
            } icon={MapPin} />
          </div>

          {/* Assignment Info */}
          <h4 className="font-medium text-gray-800 mt-5 mb-3 pt-3 border-t border-stone-100 flex items-center gap-1.5">
            <Users className="w-3.5 h-3.5 text-blue-600" /> Assignments
          </h4>
          <div className="space-y-0.5 divide-y divide-gray-50">
            <InfoRow label="Assigned Agent" value={profile.assignedAgent || '-'} />
            <InfoRow label="Assigned Counselor" value={profile.assignedCounselor || '-'} />
            <InfoRow label="Referred By" value={profile.referredBy || '-'} />
          </div>
        </SectionCard>
      </div>

      {/* Student Documents */}
      <SectionCard title="Student Documents (Profile)" icon={FileText}>
        {docEntries.some(d => d.url) ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {docEntries.map(doc => (
              <div key={doc.key} className={`p-3 rounded-lg border ${doc.url ? 'bg-green-50 border-green-200' : 'bg-stone-50 border-stone-200'}`}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <FileText className={`w-4 h-4 ${doc.url ? 'text-green-600' : 'text-gray-400'}`} />
                    <span className="text-sm font-medium text-gray-700">{doc.label}</span>
                  </div>
                  {doc.url ? (
                    <a href={doc.url} target="_blank" rel="noopener noreferrer" className="p-1 text-green-600 hover:text-green-700">
                      <Download className="w-4 h-4" />
                    </a>
                  ) : (
                    <span className="text-[10px] text-gray-400">Not uploaded</span>
                  )}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-gray-500 text-sm text-center py-4">No profile documents uploaded by the student.</p>
        )}
      </SectionCard>

      {/* Service Requests */}
      {serviceRequests.length > 0 && (
        <SectionCard title={`Applied Services (${serviceRequests.length})`} icon={FileText}>
          <div className="space-y-2">
            {serviceRequests.map((sr, idx) => (
              <div key={sr.serviceRequestId || idx} className="flex items-center justify-between p-3 bg-stone-50 rounded-lg">
                <div>
                  <p className="text-sm font-medium text-gray-900">{sr.serviceType?.replace(/_/g, ' ')}</p>
                  <p className="text-xs text-gray-500">{new Date(sr.createdAt).toLocaleDateString()} &middot; {sr.status}</p>
                </div>
                <Badge variant="outline" className="text-[10px]">{sr.status}</Badge>
              </div>
            ))}
          </div>
        </SectionCard>
      )}
    </div>
  );
}
